/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, Redirect } from "react-router-dom";

import { ActionButton, Pivot, PivotItem, Spinner, SpinnerSize, Stack } from "@fluentui/react";

import { useAuth } from "Authentication/AuthProvider";
import MostValuableProfessionalComments from "Components/MostValuableProfessionalComments/MostValuableProfessionalComments";
import MostValuableProfessionalDistributionLists from "Components/MostValuableProfessionalDistributionLists/MostValuableProfessionalDistributionLists";
import MostValuableProfessionalProfile from "Components/MostValuableProfessionalProfile/MostValuableProfessionalProfile";
import MostValuableProfessionalProgramProfile from "Components/MostValuableProfessionalProgramProfile/MostValuableProfessionalProgramProfile";
import ProfileHeader from "Components/ProfileHeader/ProfileHeader";
import { ProfileTabs } from "Constants/ProfileConstants";
import { PermissionsEnum, RolesEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { DetailWrapper } from "Containers/Common/CommonContainer.styles";
import {
  fetchUserProfile,
  userProfilePrimaryEmailUpdate,
  userProfilePrimaryEmailUpdateReset,
} from "Dataflows/MVP/MVPAction";
import {
  getFetchUserProfileErrorMessage,
  getSelectedUserProfile,
  getUserProfileIsLoadingStatus,
  getUserProfileUpdatePrimaryEmailErrorMessage,
  getUserProfileUpdatePrimaryEmailStatus,
} from "Dataflows/MVP/UserProfile/MVPUserProfileSelector";
import { useProfileTabs } from "Utility/CustomTabHooks";

import MostValuableProfessionalBenefitDetails from "../../../Components/MostValuableProfessionalProfileBenefits/MostValuableProfessionalBenefitDetails";
import {
  containerStackTokens,
  wrapperStackStyles,
} from "./MostValuableProfessionalDetailsContainer.styles";

/**
 * The Most Valuable Professional Details Container component
 * @method
 * @returns {ReactElement} The Most Valuable Professional Details Container component
 */
const MostValuableProfessionalDetailsContainer = (): ReactElement => {
  const dispatch = useDispatch();

  const { isUserLoggedIn, loading, hasAnyRoles, hasPermission, hasRole } = useAuth();
  const history = useHistory();
  const isUserProfileLoading = useSelector(getUserProfileIsLoadingStatus);
  const fetchErrorMessage = useSelector(getFetchUserProfileErrorMessage);
  const partialProfileInfo = useSelector(getSelectedUserProfile);

  const updatePrimaryEmailStatus = useSelector(getUserProfileUpdatePrimaryEmailStatus);

  const updatePrimaryEmailError = useSelector(getUserProfileUpdatePrimaryEmailErrorMessage);

  const onPrimaryEmailUpdate = (userProfileId: number, newEmail: string): void => {
    dispatch(userProfilePrimaryEmailUpdate(userProfileId, newEmail));
  };

  const onPrimaryEmailUpdateReset = (): void => {
    dispatch(userProfilePrimaryEmailUpdateReset());
  };

  const loadUserProfile = (userProfileId: number): void => {
    dispatch(fetchUserProfile(userProfileId));
  };

  if (isUserLoggedIn && !hasAnyRoles && !loading) {
    return <Redirect to="/unauthorized" />;
  }

  // We don't want MVP Tech Area Validators / Product Group Partners to be able to see Benefits.
  const canViewBenefits =
    hasPermission(TenantTypesEnum.MVP, PermissionsEnum.MVP_VIEW_BENEFITS) &&
    (!hasRole(RolesEnum.MVP_TECH_AREA_VALIDATOR) ||
      hasRole(RolesEnum.MVP_PROGRAM_OWNER) ||
      hasRole(RolesEnum.MVP_BENEFITS_MANAGER) ||
      hasRole(RolesEnum.MVP_REVIEWER));
  const canViewDistributionLists = hasPermission(
    TenantTypesEnum.MVP,
    PermissionsEnum.MVP_VIEW_DISTRIBUTIONS_LIST
  );

  const canUpdatePrimaryEmail =
    (hasRole(RolesEnum.MVP_PROGRAM_OWNER) || hasRole(RolesEnum.MVP_BENEFITS_MANAGER)) &&
    hasPermission(TenantTypesEnum.MVP, PermissionsEnum.MVP_UPDATE_PRIMARY_EMAIL);

  const { id } = useParams<{ id: string }>();
  const { selectedTab, setSelectedTab } = useProfileTabs();

  useEffect(() => {
    dispatch(fetchUserProfile(Number.parseInt(id)));
  }, [dispatch, id]);

  /**
   * Handler for when the back button is clicked.
   * @returns {void} void no return for function.
   */
  const onBackClickEvent = (): void => {
    history.goBack();
  };

  return (
    <Stack styles={wrapperStackStyles}>
      <Stack>
        <ActionButton
          iconProps={{ iconName: "RevToggleKey" }}
          ariaLabel="Back"
          onClick={onBackClickEvent}
        >
          Back
        </ActionButton>
      </Stack>
      {isUserProfileLoading ? (
        <Spinner size={SpinnerSize.large} />
      ) : !!fetchErrorMessage ? (
        <>Unable to load user at this time.</>
      ) : (
        <Stack tokens={containerStackTokens}>
          {!!partialProfileInfo && (
            <ProfileHeader
              userProfileId={partialProfileInfo.id}
              firstName={partialProfileInfo.firstName}
              lastName={partialProfileInfo.lastName}
              primaryEmail={partialProfileInfo.siteEmailAddress}
              secondaryEmail={partialProfileInfo.preferredContactEmailAddress}
              tenants={partialProfileInfo.tenants}
              currentTenant={TenantTypesEnum.MVP}
              isOvdfBlock={partialProfileInfo?.isOvdfBlock}
              isPrivate={partialProfileInfo?.isPrivate}
              canUpdatePrimaryEmail={canUpdatePrimaryEmail}
              updatePrimaryEmailStatus={updatePrimaryEmailStatus}
              updatePrimaryEmailError={updatePrimaryEmailError}
              onPrimaryEmailUpdate={onPrimaryEmailUpdate}
              onPrimaryEmailUpdateReset={onPrimaryEmailUpdateReset}
              loadUserProfile={loadUserProfile}
            />
          )}
          <Stack>
            <DetailWrapper>
              <Pivot
                selectedKey={selectedTab}
                onLinkClick={(item: PivotItem | undefined): void => {
                  setSelectedTab((item?.props.itemKey as ProfileTabs) || ProfileTabs.Profile);
                }}
              >
                <PivotItem headerText="Profile" itemKey={ProfileTabs.Profile}>
                  <MostValuableProfessionalProfile />
                </PivotItem>
                <PivotItem
                  headerText="Program Information"
                  itemKey={ProfileTabs.ProgramInformation}
                >
                  <MostValuableProfessionalProgramProfile userProfileId={partialProfileInfo?.id} />
                </PivotItem>
                {canViewBenefits && (
                  <PivotItem headerText="Benefits" itemKey={ProfileTabs.Benefits}>
                    <MostValuableProfessionalBenefitDetails />
                  </PivotItem>
                )}
                {canViewDistributionLists && (
                  <PivotItem
                    headerText="Distribution Lists"
                    itemKey={ProfileTabs.DistributionLists}
                  >
                    <MostValuableProfessionalDistributionLists />
                  </PivotItem>
                )}
                <PivotItem headerText="Comments" itemKey={ProfileTabs.Comments}>
                  <MostValuableProfessionalComments userProfileId={partialProfileInfo?.id ?? 0} />
                </PivotItem>
              </Pivot>
            </DetailWrapper>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default MostValuableProfessionalDetailsContainer;
